/**
 * Form helpers for create/edit subject views.
 */
import type { EducationCycle } from '@/types/level.types';
import type { CreateSubjectDto, Subject, UpdateSubjectDto } from '@/types/subject.types';
import { getErrorMessage } from '@/utils/errors-utils';

/** Subject id, label, and optional code used for alias matching. */
type SubjectLike = { id: string; label: string; code?: string | null };

/** Empty create form; cycle stays unset until the user picks one. */
export function emptyCreateSubjectForm(): CreateSubjectDto {
  return {
    code: '',
    label: '',
    description: '',
    educationCycle: undefined,
    isCore: false,
    status: 'active',
  };
}

/** Patch one field. */
export function applySubjectFormField<T extends CreateSubjectDto | UpdateSubjectDto>(
  form: T,
  field: keyof T,
  value: string | number | boolean | undefined,
): T {
  return { ...form, [field]: value };
}

/** Empty {@link Select} value → `undefined` (omit from the DTO). */
export function resolveEducationCycle(value: string): EducationCycle | undefined {
  return value ? (value as EducationCycle) : undefined;
}

/** Hydrate the edit form from GET subject. */
export function subjectToFormData(subject: Subject): UpdateSubjectDto {
  return {
    code: subject.code,
    label: subject.label,
    description: subject.description || '',
    educationCycle: subject.educationCycle || undefined,
    isCore: subject.isCore,
    status: subject.status,
  };
}

/** Central error copy from {@link getErrorMessage}. */
export function getSubjectApiError(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** Normalized aliases per preferred default, in display order. */
const DEFAULT_SUBJECT_ALIASES: ReadonlyArray<{ aliases: readonly string[] }> = [
  { aliases: ['français', 'francais', 'fr', 'french'] },
  { aliases: ['math', 'maths', 'mathématiques', 'mathematiques', 'mathematics'] },
  {
    aliases: [
      'hg',
      'histoire-géographie',
      'histoire-geographie',
      'histoire geographie',
      'histoire geo',
      'histoire',
      'hggsp',
    ],
  },
  {
    aliases: [
      'svt',
      'sciences de la vie et de la terre',
      'science experimental',
      'sciences expérimentales',
      'sciences experimentales',
    ],
  },
  { aliases: ['anglais', 'english', 'eng'] },
];

/** Lowercases and strips diacritics for subject-name comparison. */
function normalizeSubjectText(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
}

/** Finds the first subject whose label or code matches any alias. */
function findMatchingSubject(
  subjects: SubjectLike[],
  aliases: readonly string[],
  usedIds: Set<string>,
): SubjectLike | undefined {
  const normalizedAliases = aliases.map(normalizeSubjectText);

  for (const subject of subjects) {
    if (usedIds.has(subject.id)) continue;
    const label = normalizeSubjectText(subject.label);
    const code = normalizeSubjectText(subject.code ?? '');
    if (normalizedAliases.includes(label) || normalizedAliases.includes(code)) {
      return subject;
    }
  }

  for (const subject of subjects) {
    if (usedIds.has(subject.id)) continue;
    const label = normalizeSubjectText(subject.label);
    if (normalizedAliases.some((alias) => alias.length >= 4 && label.startsWith(alias))) {
      return subject;
    }
  }

  return undefined;
}

/** Resolve default create subject IDs from the company subjects catalogue (order preserved). */
export function resolveDefaultCreateSubjectIds(subjects: SubjectLike[]): string[] {
  if (!subjects.length) return [];

  const usedIds = new Set<string>();
  const ids: string[] = [];

  for (const { aliases } of DEFAULT_SUBJECT_ALIASES) {
    const match = findMatchingSubject(subjects, aliases, usedIds);
    if (!match) continue;
    usedIds.add(match.id);
    ids.push(match.id);
  }

  return ids;
}
